import { DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ItemPerPage } from '@core/models/file-response.model';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';
import { SuperAdminService } from '@core/services/super-admin.service';
import { SurveyService } from '@core/services/survey.service';
import { CommonFunctions } from '@shared/common-functions';
import { END_DATE_GREATER, itemsPerPageList, ratings, SELECT_BOTH_DATES } from '@shared/constant/shared.constant';
import { BreadcrumbItem, CompanyService } from '@shared/services/company.service';
import { ToastService } from '@shared/services/toast.service';
import { autoUnsubWith, Utility } from '@shared/services/utility.abstract';

@Component({
  selector: 'app-survey-list',
  templateUrl: './survey-list.component.html',
  styleUrls: ['./survey-list.component.scss']
})
export class SurveyListComponent implements OnInit{
  readonly destroyRef = inject(DestroyRef);
  dummy: string = '../../../../assets/images/dummy-img.png';
  filterServeyDateForm: FormGroup;
  customItemPerPageBy: ItemPerPage[] = itemsPerPageList;
  parkingLotByStatesListWithAll: any[] = [];
  parkingLotByStatesList: any[] = [];
  surveyList: any[] = [];
  surveyListTotalRecords: number = 0;
  searchItemPerPageBy = null;
  itemsPerPage: number = 10;
  currentSkip: number = 0;
  pageNumber: number = 1;
  companyId: string = '';
  roleRoute: string = '';
  roleName: string = '';
  companyName: string = '';
  ratings = ratings
  exportFile: any [];

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly companyService: CompanyService,
    private readonly superAdminService: SuperAdminService,
    private readonly router: Router,
    private readonly surveyService: SurveyService,
    private readonly datePipe: DatePipe,
    private readonly toastService: ToastService,
    private readonly encryptStorage: EncryptionStorageService
  ) { }

  ngOnInit() {
    this.roleName = this.encryptStorage.getItem('roleName') || '';
    this.roleRoute = CommonFunctions.routeToBookingDetail(this.roleName);
    this.route.params.subscribe((params: Params) => {
      this.companyId = params['id'];
      const breadcrumbObj: BreadcrumbItem[] = [
        {
          title: 'Experience Survey',
        },
      ];
      this.companyService.setAdminHeaders(this.companyId, breadcrumbObj);
      this.companyId = this.encryptStorage.getItem('companyId');
      this.companyName = this.encryptStorage.getItem('companyName');
      this.getParkingLotByStatesName();
      this.initializeSurveyFilterForm();
    });
  }

  initializeSurveyFilterForm() {
    this.filterServeyDateForm = this.formBuilder.group({
      parkingLots: [[], Validators.required],
      name: [''],
      userInvoice: [''],
      startDate: [''],
      endDate: [''],
      SearchItemPerPageBy: [this.itemsPerPage],
      invoiceNumber: ['']
    })
  }

  getParkingLotByStatesName(_initial: boolean = false) {
    const req = {
      companyIds: [this.companyId]
    };

    this.superAdminService.getParkingLotNamesByCompanyIds(req)
      .pipe(autoUnsubWith(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.parkingLotByStatesList = response.data;
          this.parkingLotByStatesListWithAll = [...this.parkingLotByStatesList];
          const selectedParkingLot = history.state?.selectedParkingLot;
          const selectedIds = Array.isArray(selectedParkingLot) ? selectedParkingLot : [selectedParkingLot];
          const validSelectedLots = this.parkingLotByStatesList.filter(lot => selectedIds.includes(lot.id));
          this.filterServeyDateForm.controls['parkingLots'].setValue(
            validSelectedLots.length ? validSelectedLots : [...this.parkingLotByStatesList]
          );
          this.GetParkingLotSurveyDetailsByFilters('onCompanyChange');
        },
        error: (_error: any) => {
          this.parkingLotByStatesListWithAll = [];
          this.filterServeyDateForm.controls['parkingLots'].setValue([]);
        },
      });
  }

  formatDateToString(date: Date): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd');
  }

  GetParkingLotSurveyDetailsByFilters(changeAction:string) {
    const startDate = this.filterServeyDateForm.value.startDate;
    const endDate = this.filterServeyDateForm.value.endDate;
    
    if (!this.validateDateRange(startDate, endDate)) {
      return; // Stop further execution
    }
    
    if(changeAction === 'search' || changeAction === 'onCompanyChange'){
      this.currentSkip = 0;
      this.pageNumber = 1;
    }

    const surveyListRequest = {
      companyIds: null,
      parkingLotIds: this.filterServeyDateForm?.value?.parkingLots?.map(item => item.id).filter(id => id !== null && id !== undefined),
      name: this.filterServeyDateForm.value.name,
      invoiceNumber: this.filterServeyDateForm.value.invoiceNumber,
      startDate: this.formatDateToString(this.filterServeyDateForm.value.startDate) || '',
      endDate: this.formatDateToString(this.filterServeyDateForm.value.endDate) || '',
      timeZone: Utility.getLocalTimeZone(),
      pageNumber: this.pageNumber,
      pageSize: this.itemsPerPage
    }
    // Call the service to get the filtered survey details
    this.surveyService.getParkingLotSurveyDetailsByFilters(surveyListRequest)
      .pipe(autoUnsubWith(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.surveyList = response?.data.surveyList;
          this.surveyListTotalRecords = response?.data?.totalRecords;
        },
        error: (_error: any) => {
          this.surveyList = [];
          this.surveyListTotalRecords = 0;
        },
      });
  }

  private validateDateRange(startDate: Date | null, endDate: Date | null): boolean {
    if ((startDate && !endDate) || (endDate && !startDate)) {
      this.toastService.showToastMessage('error', 'Error', SELECT_BOTH_DATES);
      return false;
    }

    if (startDate && endDate && startDate > endDate) {
      this.toastService.showToastMessage('error', 'Error', END_DATE_GREATER);
      return false;
    }

    return true;
  }

  getSelectedRating(parkingExperinceRating: number) {
    return CommonFunctions.getSelectedRating(parkingExperinceRating);
  }

  onPageChange(event) {
    this.currentSkip = event.first;
    this.pageNumber = event.page + 1;
    CommonFunctions.scrollTopRow('top');
    this.GetParkingLotSurveyDetailsByFilters('onPageChange');
  }

  onDropdownChange(event: any) {
    this.itemsPerPage = event.value;
    this.currentSkip = 1;
    this.pageNumber = 1;
    CommonFunctions.scrollTopRow('top');
    this.GetParkingLotSurveyDetailsByFilters("onDropdownChange");

  }

  resetSurveyListFilter() {
    this.filterServeyDateForm.reset();
    this.filterServeyDateForm.controls['parkingLots'].setValue([...this.parkingLotByStatesList]);
    this.currentSkip = 0;
    this.pageNumber = 1;
    this.itemsPerPage = 10;
    this.searchItemPerPageBy = this.customItemPerPageBy[0];
    this.itemsPerPage = this.searchItemPerPageBy.value;
    this.GetParkingLotSurveyDetailsByFilters('reset');
  }

  navigateToBookingDetailsScreen(userType: string, bookingId: string) {
    const routePath = userType === 'Driver' ? 'booking-details' : 'group-booking-details';
      this.router.navigate([`${this.roleRoute}/${routePath}`], {
        queryParams: {
          id: bookingId,
          assisted: false,
        },
      });
  }

  onInvoiceInput(event: any) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, ''); // remove non-numeric
    this.filterServeyDateForm.controls['invoiceNumber'].setValue(input.value);
  }

surveyListExport() {
    const surveyListRequest = {
      companyIds: null,
      parkingLotIds: this.filterServeyDateForm?.value?.parkingLots?.map(item => item.id).filter(id => id !== null && id !== undefined),
      name: this.filterServeyDateForm.value.name,
      invoiceNumber: this.filterServeyDateForm.value.invoiceNumber,
      startDate: this.formatDateToString(this.filterServeyDateForm.value.startDate) || '',
      endDate: this.formatDateToString(this.filterServeyDateForm.value.endDate) || '',
      timeZone: Utility.getLocalTimeZone(),
      pageNumber: 0,
      pageSize: 0
    }

    this.surveyService.getParkingLotSurveyDetailsByFilters(surveyListRequest).subscribe({
      next: (response: any) => {
        this.exportFile = response?.data?.surveyList;
        this.export(this.exportFile);
      }
    })

  }

  export(data) {
    if (data) {
      const headers = [
        { header: 'S.No', key: 'index', formatter: (value) => value + 1 },
        { header: 'Name', key: 'name', formatter: (value) => value ? value : '-' },
        { header: 'Parking Location', key: 'parkingLotName', formatter: (value) => value ? value : '-' },
        { header: 'Parking Address', key: 'parkinglotAddress', formatter: (value) => value ? value : '-' },
        { header: 'Invoice Number', key: 'invoiceNumber', formatter: (value) => value ? value : '-' },
        { header: 'Arrival Date & Time', key: 'startDate', formatter: (value) => value ? CommonFunctions.parseIsoWithoutTimezone(value) : '-' },
        { header: 'Departure Date & Time', key: 'endDate', formatter: (value) => value ? CommonFunctions.parseIsoWithoutTimezone(value) : '-' },
        { header: 'Submitted Date', key: 'submittedDate', formatter: (value) => value ? this.submittedFormatDate(value) : '-' },
        { header: 'Rating', key: 'parkingExperinceRating', formatter: (value) => { const rating = this.getSelectedRating(value); return rating ? rating.label : '-' } },
        { header: 'Description', key: 'parkingExperinceRating', formatter: (value) => { const rating = this.getSelectedRating(value); return rating ? rating.desc : '-'; } },
      ]
      const dataWithIndex = data?.map((item, index) => ({
        ...item,
        index, // Add the index to each row
      }));

      return CommonFunctions.exportToExcel(dataWithIndex, headers, 'Survey List_export_')
    }
  }

  submittedFormatDate(input: string): string {
    const [datePart] = input.split("T");
    const [year, month, day] = datePart.split("-");
    return `${month}-${day}-${year}`;
  }
}
